export const BASIS = [
  {
    key: 'cost_this_period',
    title: 'Project Costs',
    help: 'Calculate from costs recorded to the project during the selected accounting period.',
    sample: 100000,
    sampleLabel: '$100,000.00',
  },
  {
    key: 'gross_owner_billing_period',
    title: 'Owner Billing',
    help: 'Calculate from posted owner billing during the selected accounting period.',
    sample: 250000,
    sampleLabel: '$250,000.00',
  },
  {
    key: 'revenue_this_period',
    title: 'Revenue from This Period',
    help: 'Calculate from earned revenue recognized during the selected accounting period.',
    sample: 200000,
    sampleLabel: '$200,000.00',
  },
  {
    key: 'current_contract_value',
    title: 'Current Contract Value',
    help: 'Original contract plus approved change orders. Pending change orders are not included.',
    sample: 1000000,
    sampleLabel: '$1,000,000.00',
  },
  {
    key: 'total_commitment_amount',
    title: 'Total Commitment Amount',
    help: 'Original commitment amounts plus commitment change orders. Commitments excluded on the project are left out.',
    sample: 500000,
    sampleLabel: '$500,000.00',
  },
  {
    key: 'manual_amount',
    title: 'Amount Entered Each Period',
    help: 'Enter the amount when the calculation is prepared each month.',
    sample: 7500,
    sampleLabel: '$7,500.00',
  },
];

const BASIS_KEYS = new Set(BASIS.map((item) => item.key));

export const OPERATORS = [
  { value: 'add', label: '+' },
  { value: 'sub', label: '−' },
  { value: 'mul', label: '×' },
  { value: 'div', label: '÷' },
];

export function basisTitle(key) {
  return BASIS.find((item) => item.key === key)?.title || '';
}

export function chainFromFormula(node) {
  const terms = [];
  const operators = [];
  const appendLeaf = (current) => {
    if (current && (current.ref === 'input' || current.ref === 'parameter' || current.ref === 'named')) {
      terms.push({ kind: current.ref, key: current.key || '', value: '' });
      return;
    }
    if (current && Object.prototype.hasOwnProperty.call(current, 'const')) {
      terms.push({ kind: 'constant', key: '', value: String(current.const) });
      return;
    }
    terms.push({ kind: '', key: '', value: '' });
  };
  const walk = (current) => {
    if (current && current.op && Array.isArray(current.args) && current.args.length === 2) {
      walk(current.args[0]);
      operators.push(current.op);
      appendLeaf(current.args[1]);
      return;
    }
    appendLeaf(current);
  };
  if (!node) return { terms: [{ kind: '', key: '', value: '' }], operators: [] };
  walk(node);
  if (!terms.length) terms.push({ kind: '', key: '', value: '' });
  return { terms, operators };
}

export function viewFromDocument(document) {
  const parameters = (document?.parameters || []).map((row) => ({
    id: row.key,
    key: row.key,
    label: row.label || row.key,
    description: row.description || '',
    required: row.required !== false,
    defaultValue: row.default_value == null ? '' : String(row.default_value),
    display: row.display || 'number',
  }));
  const namedValues = (document?.named_values || []).map((row) => ({
    id: row.key,
    key: row.key,
    label: row.label || row.key,
    ...chainFromFormula(row.formula),
  }));
  return { parameters, namedValues };
}

export function basisOf(namedValues) {
  const keys = new Set();
  (namedValues || []).forEach((named) => {
    (named.terms || []).forEach((term) => {
      if (term.kind === 'input' && BASIS_KEYS.has(term.key)) keys.add(term.key);
    });
  });
  if (keys.size === 1) return [...keys][0];
  if (keys.size > 1) return 'mixed';
  return '';
}

export function needsAdvanced(namedValues) {
  return (namedValues || []).some((named) => (
    (named.terms || []).some((term) => !term.kind)
    && (named.terms || []).length > 1
    && (named.label || named.key)
  ));
}

export function percentEntry(stored) {
  if (stored == null || stored === '') return '';
  const moved = movePoint(String(stored), 2);
  return moved;
}

export function percentStored(entry) {
  if (entry == null || String(entry).trim() === '') return '';
  return movePoint(String(entry).trim(), -2);
}

export function rateEntry(parameter) {
  if (!parameter) return '';
  if (parameter.display === 'percent') return percentEntry(parameter.defaultValue);
  return parameter.defaultValue == null ? '' : String(parameter.defaultValue);
}

export function rateStored(parameter, entry) {
  if (parameter?.display === 'percent') return percentStored(entry);
  return entry;
}

export function money(value) {
  if (value == null || Number.isNaN(Number(value))) return '';
  const negative = Number(value) < 0;
  const absolute = Math.abs(Number(value));
  const text = absolute.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${negative ? '-' : ''}$${text}`;
}

export function exampleAmounts(parameters, namedValues) {
  const rates = {};
  (parameters || []).forEach((row) => {
    if (!row.key) return;
    if (row.defaultValue === '' || row.defaultValue == null) {
      rates[row.key] = { value: null, label: row.label || 'a rate' };
      return;
    }
    const raw = String(row.defaultValue);
    const number = Number(raw);
    rates[row.key] = {
      value: Number.isFinite(number) ? number : null,
      label: row.display === 'percent' ? `${percentEntry(raw)}%` : row.display === 'per_thousand' ? `$${raw} per $1,000` : raw,
    };
  });
  const named = {};
  const readable = {};
  (namedValues || []).forEach((row) => {
    const steps = [];
    let value = null;
    let complete = true;
    (row.terms || []).forEach((term, index) => {
      const piece = termValue(term, rates, named);
      if (piece.value == null) complete = false;
      if (index === 0) value = piece.value;
      else if (value != null && piece.value != null) value = applyOp(row.operators[index - 1], value, piece.value);
      else complete = false;
      steps.push(piece.label);
    });
    if (row.key) named[row.key] = complete ? value : null;
    readable[row.id] = {
      steps,
      operators: row.operators || [],
      value: complete ? value : null,
      complete,
    };
  });
  return readable;
}

export function exampleJournal(lines, namedValues, amountsById) {
  const byKey = {};
  (namedValues || []).forEach((row) => {
    byKey[row.key] = amountsById[row.id];
  });
  let debits = 0;
  let credits = 0;
  let counted = true;
  const rows = (lines || []).map((line) => {
    const amount = byKey[line.amount_key];
    if (!amount || amount.value == null) counted = false;
    const number = amount && amount.value != null ? amount.value : null;
    if (number != null) {
      if (line.side === 'credit') credits += number;
      else debits += number;
    }
    return {
      side: line.side === 'credit' ? 'CR' : 'DR',
      amountLabel: amountLabel(namedValues, line.amount_key),
      account: accountPhrase(line),
      projectCost: projectCostPhrase(line.job_cost_policy),
      example: number == null ? '' : money(number),
    };
  });
  const difference = Math.round((debits - credits) * 100) / 100;
  return {
    rows,
    balanced: counted && rows.length > 0 && Math.abs(difference) < 0.005,
    difference,
    counted,
  };
}

export function plainIssues({ name, parameters, namedValues, lines }) {
  const issues = [];
  if (!String(name || '').trim()) issues.push('Give this calculation a name.');
  const basis = basisOf(namedValues);
  if (!basis) issues.push('Choose what this calculation is based on.');
  (parameters || []).forEach((row) => {
    if (row.label && (row.defaultValue == null || row.defaultValue === '')) {
      issues.push(`${row.label} needs a default value.`);
    }
  });
  (namedValues || []).filter((row) => row.label || row.key).forEach((row) => {
    if ((row.terms || []).some((term) => !term.kind)) {
      issues.push(`${row.label || 'A calculated amount'} is missing part of its formula.`);
    }
    (row.terms || []).forEach((term) => {
      if (term.kind === 'named') {
        const earlier = (namedValues || []).find((item) => item.key === term.key);
        if (!earlier) issues.push(`${row.label || 'A calculated amount'} references a calculated amount that has not been defined.`);
      }
    });
  });
  (lines || []).forEach((line) => {
    if (!line.amount_key) issues.push('Choose which calculated amount each accounting line uses.');
    if (line.strategy === 'configured_account' && !line.account_vuid && !line.role_label) {
      issues.push(`Choose the account that should be ${line.side === 'credit' ? 'credited' : 'debited'}.`);
    }
  });
  const journal = exampleJournal(lines, namedValues, exampleAmounts(parameters, namedValues));
  if (journal.counted && journal.rows.length > 1 && !journal.balanced) {
    issues.push(`The accounting entry is out of balance by ${money(journal.difference)}.`);
  }
  return [...new Set(issues)];
}

export function templates() {
  return {
    cost: laborBurdenTemplate(),
    billing: insuranceTemplate(),
    monthly: allocationTemplate(),
  };
}

function amountKey(name) {
  const slug = String(name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return slug || 'calculated_amount';
}

function blankRate() {
  return {
    key: 'rate',
    label: 'Rate',
    description: '',
    required: true,
    display: 'percent',
    default_value: '',
    value_type: 'decimal',
  };
}

export function starterPack(kind, name) {
  const label = String(name || '').trim() || 'Calculated amount';
  const key = amountKey(label);
  if (kind === 'monthly') {
    return {
      description: '',
      document: {
        parameters: [],
        named_values: [{
          key,
          label,
          formula: { ref: 'input', key: 'manual_amount' },
        }],
      },
      distribution: twoSided(key, 'always', `${key}_offset`, ''),
    };
  }
  const basis = kind === 'billing' ? 'gross_owner_billing_period' : kind === 'cost' ? 'cost_this_period' : '';
  if (!basis) return null;
  return {
    description: '',
    document: {
      parameters: [blankRate()],
      named_values: [{
        key,
        label,
        formula: {
          op: 'mul',
          args: [
            { ref: 'input', key: basis },
            { ref: 'parameter', key: 'rate' },
          ],
        },
      }],
    },
    distribution: twoSided(key, 'always', `${key}_offset`, ''),
  };
}

function laborBurdenTemplate() {
  return {
    name: 'Labor Burden',
    description: 'Adds labor burden to selected project labor costs.',
    document: {
      parameters: [{
        key: 'burden_rate',
        label: 'Burden Rate',
        description: 'Each project can use its own Burden Rate. The default is used when the calculation is first assigned.',
        required: true,
        display: 'percent',
        default_value: '0.12',
        value_type: 'decimal',
      }],
      named_values: [{
        key: 'labor_burden',
        label: 'Labor Burden',
        formula: {
          op: 'mul',
          args: [
            { ref: 'input', key: 'cost_this_period' },
            { ref: 'parameter', key: 'burden_rate' },
          ],
        },
      }],
    },
    distribution: twoSided('labor_burden', 'always', 'labor_burden_recovery', 'Labor Burden Recovery'),
  };
}

function insuranceTemplate() {
  const billing = (rateKey) => ({
    op: 'mul',
    args: [
      { op: 'div', args: [{ ref: 'input', key: 'gross_owner_billing_period' }, { const: '1000' }] },
      { ref: 'parameter', key: rateKey },
    ],
  });
  return {
    name: 'GL Insurance',
    description: 'Calculates premium, accrual, and recovery from owner billing.',
    document: {
      parameters: [
        {
          key: 'insurance_rate',
          label: 'Insurance Rate',
          description: 'Dollars of premium per $1,000 of owner billing.',
          required: true,
          display: 'per_thousand',
          default_value: '6',
          value_type: 'decimal',
        },
        {
          key: 'accrual_rate',
          label: 'Accrual Rate',
          description: 'Dollars of accrual per $1,000 of owner billing.',
          required: true,
          display: 'per_thousand',
          default_value: '4',
          value_type: 'decimal',
        },
      ],
      named_values: [
        { key: 'premium', label: 'Premium', formula: billing('insurance_rate') },
        { key: 'accrual', label: 'Accrual', formula: billing('accrual_rate') },
        {
          key: 'recovery',
          label: 'Recovery',
          formula: { op: 'sub', args: [{ ref: 'named', key: 'premium' }, { ref: 'named', key: 'accrual' }] },
        },
      ],
    },
    distribution: {
      lines: [
        line(1, 'debit', 'premium', 'project_choice', 'cost_type_expense', '', 'Project destination expense account'),
        resolverLine(2, 'credit', 'recovery', 'insurance_recovery', 'Insurance Recovery'),
        resolverLine(3, 'credit', 'accrual', 'accrued_insurance', 'Accrued Insurance'),
      ],
    },
  };
}

function allocationTemplate() {
  return {
    name: 'PM Allocation',
    description: 'Enter the PM allocation amount each month and add it to the project.',
    document: {
      parameters: [],
      named_values: [{
        key: 'pm_allocation',
        label: 'PM Allocation',
        formula: { ref: 'input', key: 'manual_amount' },
      }],
    },
    distribution: twoSided('pm_allocation', 'always', 'pm_allocation_recovery', 'PM Allocation Recovery'),
  };
}

function twoSided(amountKey, policy, roleKey, roleLabel) {
  return {
    lines: [
      line(1, 'debit', amountKey, policy, 'cost_type_expense', '', 'Project destination expense account'),
      line(2, 'credit', amountKey, 'never', 'configured_account', roleKey, roleLabel),
    ],
  };
}

function line(number, side, amountKey, policy, strategy, roleKey, roleLabel) {
  return {
    line_number: number,
    side,
    amount_key: amountKey,
    line_key: amountKey === 'labor_burden' && side === 'debit' ? 'labor_burden' : `${amountKey}-${side}`,
    job_cost_policy: policy,
    creates_project_cost: policy === 'always',
    memo: '',
    account: {
      strategy,
      role_key: roleKey,
      role_label: roleLabel,
      account_vuid: null,
      resolver: null,
    },
  };
}

function resolverLine(number, side, amountKey, resolver, label) {
  return {
    line_number: number,
    side,
    amount_key: amountKey,
    line_key: `${amountKey}-${side}`,
    job_cost_policy: 'never',
    creates_project_cost: false,
    memo: '',
    account: {
      strategy: 'named_resolver',
      resolver,
      role_key: resolver,
      role_label: label,
      account_vuid: null,
    },
  };
}

function projectCostPhrase(policy) {
  if (policy === 'always') return 'Create Project Cost';
  if (policy === 'project_choice') return 'Let each project decide';
  return 'Journal Entry Only';
}

function accountPhrase(line) {
  if (line.strategy === 'cost_type_expense') return 'Project destination expense account';
  if (line.strategy === 'insurance_recovery') return line.role_label || 'Insurance Recovery';
  if (line.strategy === 'accrued_insurance') return line.role_label || 'Accrued Insurance';
  return line.role_label || 'Specific GL account';
}

function amountLabel(namedValues, key) {
  return (namedValues || []).find((row) => row.key === key)?.label || 'Calculated amount';
}

function termValue(term, rates, named) {
  if (term.kind === 'input' && BASIS_KEYS.has(term.key)) {
    const basis = BASIS.find((item) => item.key === term.key);
    return { value: basis.sample, label: basis.sampleLabel };
  }
  if (term.kind === 'parameter') {
    const rate = rates[term.key];
    return { value: rate ? rate.value : null, label: rate?.label || 'a rate' };
  }
  if (term.kind === 'constant') {
    const value = Number(term.value);
    return { value: Number.isFinite(value) ? value : null, label: term.value || 'a number' };
  }
  if (term.kind === 'named') {
    const value = named[term.key];
    return { value: value == null ? null : value, label: value == null ? 'an earlier amount' : money(value) };
  }
  return { value: null, label: '…' };
}

function applyOp(op, left, right) {
  if (op === 'add') return left + right;
  if (op === 'sub') return left - right;
  if (op === 'mul') return left * right;
  if (op === 'div') return right === 0 ? null : left / right;
  return null;
}

function movePoint(raw, places) {
  const text = String(raw).trim();
  if (!/^-?\d+(\.\d+)?$/.test(text)) return text;
  const negative = text.startsWith('-');
  const unsigned = negative ? text.slice(1) : text;
  const [whole, frac = ''] = unsigned.split('.');
  const digits = `${whole}${frac}`;
  const point = whole.length + places;
  const padded = point > digits.length ? digits.padEnd(point, '0') : digits;
  const leftRaw = point <= 0 ? '0' : padded.slice(0, point);
  const rightRaw = point <= 0 ? `${'0'.repeat(-point)}${padded}` : padded.slice(point);
  let left = leftRaw.replace(/^0+(?=\d)/, '');
  if (!left) left = '0';
  const right = rightRaw.replace(/0+$/, '');
  return `${negative ? '-' : ''}${left}${right ? `.${right}` : ''}`;
}
